import React from "react";
import PropTypes from "prop-types";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";

/**
 * SelectInput — Syncfusion DropDownList wrapper with project-standard styling.
 *
 * Uses the outline variant with floating labels for consistent form appearance.
 *
 * @example
 * <SelectInput
 *   placeholder="Select a locale..."
 *   dataSource={[{ text: "English", value: "en-US" }, { text: "Spanish", value: "es-ES" }]}
 *   value={value}
 *   onChange={onChange}
 * />
 */
export default function SelectInput({
  placeholder,
  dataSource = [],
  fields = { text: "text", value: "value" },
  value,
  onChange,
  error,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  return (
    <div className={classNames("select-input-wrapper", className)}>
      <DropDownListComponent
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        dataSource={dataSource}
        fields={fields}
        value={value}
        change={e => onChange?.(e.itemData?.[fields.value ?? "value"] ?? e.value)}
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

SelectInput.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Array of items */
  dataSource: PropTypes.array,
  /** Field mapping — defaults to { text: "text", value: "value" } */
  fields: PropTypes.object,
  /** Controlled value */
  value: PropTypes.any,
  /** Change handler — receives the selected value */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Fully disabled */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
  /** Syncfusion float-label type */
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

